import { useState } from 'react';

import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';

import { Close } from '@mui/icons-material';
import {
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
  Theme,
  SxProps,
  Box,
} from '@mui/material';

import { BackrsForm } from './BackrsForm';
import { BackrsFormField, BackrsFormValues } from './types';
import { handlePhoneLogin } from '../Login/login.util';

const LOGIN_STRINGS = appStrings.modules.login;

type ResendCodeProps = Readonly<{
  environmentUrl: string;
}>;

const defaultForgotPasswordValues: BackrsFormValues = {
  forgotPasswordEmail: '',
};

export const ResendCode = ({ environmentUrl }: ResendCodeProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const fields: BackrsFormField[] = [
    {
      id: 'phone',
      name: 'phone',
      placeholder: LOGIN_STRINGS.phonePlaceholder,
      type: 'tel',
      autoComplete: 'phone',
      icon: {
        type: 'LEFT_ICON',
        leftIcon: 'Phone',
      },
      inputProps: { maxLength: 10 },
      rules: {
        pattern: { value: /^\d{10}$/, message: LOGIN_STRINGS.error.invalidPhone },
      },
    },
  ];

  const handleSubmission = async (data: BackrsFormValues) => {
    try {
      await handlePhoneLogin(data.phone, environmentUrl);

      // todo: use react-hot-toast after sending successful pw reset
    } catch {
      console.log('Error resending verification code');
    } finally {
      closeModal();
    }
  };

  return (
    <>
      <ButtonBase sx={noCodeStyle} disableRipple>
        <Typography
          variant="text-sm-regular"
          color={(theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
        >
          {LOGIN_STRINGS.otp.didntReceiveText}{' '}
          <Box component="span" onClick={openModal} sx={clickToResendSx}>
            {LOGIN_STRINGS.otp.clickToResend}
          </Box>
        </Typography>
      </ButtonBase>
      <Dialog
        PaperProps={{
          style: { borderRadius: 8 },
        }}
        open={isOpen}
        onClose={closeModal}
      >
        <DialogTitle mt={2}>
          <IconButton aria-label="close" onClick={closeModal} sx={dialogCloseButtonStyle}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>{LOGIN_STRINGS.otp.resendCode}</Typography>
          <BackrsForm
            fields={fields}
            defaultValues={defaultForgotPasswordValues}
            buttonLabel="Send"
            onSubmit={handleSubmission}
          >
            <Typography sx={contactSupportTextStyle}>
              {LOGIN_STRINGS.otp.contactSupportPrompt}
              <Link
                href="mailto:support@backrs.com?subject=Backrs App - Contact Request"
                sx={contactSupportLinkStyle}
              >
                {LOGIN_STRINGS.otp.contactSupportButton}
              </Link>
            </Typography>
          </BackrsForm>
        </DialogContent>
      </Dialog>
    </>
  );
};

const clickToResendSx: SxProps<Theme> = ({ backrs, typography }) => ({
  color: backrs.colors['Brand/700'],
  cursor: 'pointer',
  ...typography['text-sm-semibold'],
});

const noCodeStyle: SxProps<Theme> = ({ backrs }) => ({
  my: backrs.spacing['spacing-4xl'],
});

const contactSupportTextStyle: SxProps<Theme> = {
  fontSize: '0.8125rem',
  textAlign: 'center',
  mt: 1,
} as const;

const contactSupportLinkStyle: SxProps<Theme> = {
  color: appColors.color.navy2,
  fontWeight: 700,
  textDecoration: 'none',
  cursor: 'pointer',
} as const;

const dialogCloseButtonStyle: SxProps<Theme> = {
  position: 'absolute',
  right: 8,
  top: 8,
} as const;
